import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles, withStyles } from '@mui/styles';
import { Badge, Tooltip } from '@mui/material';
import clsx from 'clsx';

import palette from '../../../components/styles/palette';
import { requestIconType } from '../../../enums/requestTypes';

import MarkerContainer from '../MarkerContainer';
import Remove from '../../icons/Remove';
import { Add, ParcelIcon } from '../../icons';
import Defer from '../../icons/Defer';
import Collection from '../../icons/Collection';
import AddRemove from '../../icons/AddRemove';
import RequestMark from '../../icons/RequestMark';
import WarningIcon from '../../icons/Warning';
import { Text, View } from '../../Base';

import {
  calculateDeliveryStopType,
  getStopPickupKind,
  isCollectionStop,
} from '../../../models/stop';
import { hasAnyExceptions } from '../../../models/routeDetail';
import {
  generateParcelFromRequest,
  getAPRequests,
  getRagStatusColor,
  getRouteOutstandingRequest,
  getStopRequestType,
  isHighValueParcel,
  isPharmaParcel,
  isSmallsToteParcel,
} from '../../../features/routeDetail/model';
import { getZoomLevel } from '../../../store/selectors/map';
import { DELIVERY_STOP_TYPES } from '../../modals/SelectByCriteria/constants';
import {
  HIGH_VALUE_PARCEL,
  PHARMA_PARCEL,
  TOTE_PARCEL,
} from '../../../constants/strings';
import { ConditionalWrapper } from '../../ConditionalWrapper';
import { pickupKindTypesLabels } from '../../../enums/stopTypes';

const useStyles = makeStyles(({ typography }) => {
  const { pxToRem } = typography;
  return {
    container: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: pxToRem(5),
      height: pxToRem(26),
    },
    smallContainer: {
      width: pxToRem(26),
    },
    detailedContainer: {
      width: pxToRem(68),
      position: 'relative',
      '& .notEmpty + .smallText': {
        paddingRight: pxToRem(0),
      },
    },
    activeContainer: {
      backgroundColor: palette.selectedStopMarker,
      '&::after': {
        borderTop: `13px solid ${palette.selectedStopMarker}`,
      },
    },
    textValue: {
      fontFamily: 'Roboto',
      textAlign: 'center',
      letterSpacing: '-0.02em',
      textTransform: 'capitalize',
      color: '#FFFFFF',
      display: 'flex',
      flex: '1',
      justifyContent: 'center',
      position: 'absolute',
      '&.smallText': {
        fontSize: pxToRem(9),
        paddingRight: pxToRem(4),
      },
    },
    smallContainerTextValue: {
      fontWeight: 500,
      fontSize: pxToRem(14),
      lineHeight: pxToRem(20),
    },
    detailedContainerTextValue: {
      fontWeight: 400,
      fontSize: pxToRem(10),
      lineHeight: pxToRem(16),
    },
    exceptionContainer: {
      width: pxToRem(12),
      backgroundColor: palette.dpdBlack,
      display: 'flex',
      height: '100%',
      borderBottomRightRadius: pxToRem(5),
      borderTopRightRadius: pxToRem(5),
      justifyContent: 'space-evenly',
      alignItems: 'center',
      flexDirection: 'column',
    },
    yellowContainer: {
      backgroundColor: palette.yellow,
    },
    rightContent: {
      position: 'absolute',
      right: 0,
    },
    leftContent: {
      position: 'absolute',
      left: 0,
    },
    collectionIcon: {
      width: pxToRem(16),
      height: pxToRem(16),
      display: 'flex',
      flex: '1',
      justifyContent: 'center',
      position: 'absolute',
    },
    warningIcon: {
      width: pxToRem(10),
      height: pxToRem(10),
      fill: palette.typography.white.solid,
    },
    requestMark: {
      width: pxToRem(7),
      height: pxToRem(9),
    },
    requestIcons: {
      width: pxToRem(7),
      height: pxToRem(8),
      fill: palette.typography.white.solid,
    },
    deferIcon: {
      width: pxToRem(10),
      height: pxToRem(10),
    },
    dpdIcon: {
      display: 'flex',
      flex: '1',
      justifyContent: 'center',
      position: 'absolute',
      '& svg': {
        width: typography.pxToRem(16),
        height: typography.pxToRem(16),
        fill: palette.typography.white.solid,
      },
    },
    highValueContainer: {
      width: typography.pxToRem(4),
      height: '100%',
      borderRight: `2px solid ${palette.typography.white.solid}`,
    },
    cellBlue: {
      backgroundColor: palette.statuses.statusBlue,
    },
    cellOrange: {
      backgroundColor: palette.modalButtons.orange,
    },
    cellGreen: {
      backgroundColor: palette.statuses.statusGreen,
    },
    fullHeighContainer: {
      height: '100%',
      display: 'flex',
    },
    exceptionWrapper: {
      width: pxToRem(12),
    },
    roundLeftBorder: {
      borderRadius: '5px 0px 0px 5px',
    },
  };
});

const StyledBadge = withStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: palette.typography.black.solid,
  },
  badge: {
    background: '#FFFFFF',
    fontFamily: 'Roboto',
    fontWeight: 700,
    fontSize: '8px',
    lineHeight: '8px',
    minHeight: '16px',
    minWidth: '16px',
    padding: 0,
    borderRadius: '50%',
    height: '16px',
  },
}))(Badge);

// eslint-disable-next-line react/display-name
const ExceptionContainer = memo(({ stop }) => {
  const styles = useStyles();
  const hasRequest = !!stop.requests.length;

  const getRequestTypeIcon = useCallback((type) => {
    switch (type) {
      case requestIconType.MIXED:
        return AddRemove;
      case requestIconType.ADD:
        return Add;
      case requestIconType.REMOVE:
        return Remove;
      case requestIconType.DEFFER:
        return Defer;
      default:
        return <></>;
    }
  }, []);

  const getRequestContent = useCallback(() => {
    const requestType = getStopRequestType(stop.requests);
    const RequestTypeIcon = getRequestTypeIcon(requestType.type);
    return (
      <>
        <RequestMark className={styles.requestMark} />
        <RequestTypeIcon
          className={clsx(
            styles.requestIcons,
            requestType.type === requestIconType.DEFFER && styles.deferIcon
          )}
          fill="none"
          color={palette.goldenGrass}
        />
      </>
    );
  }, [
    stop.requests,
    getRequestTypeIcon,
    styles.requestMark,
    styles.requestIcons,
    styles.deferIcon,
  ]);

  return (
    <>
      {hasRequest ? (
        getRequestContent()
      ) : (
        <WarningIcon className={styles.warningIcon} />
      )}
    </>
  );
});

// eslint-disable-next-line react/display-name
const HighValueItemContainer = memo(({ stop }) => {
  const styles = useStyles();
  let values = [];
  let tipItems = [];
  const APRequests = getAPRequests(stop);
  const additionalParcels = generateParcelFromRequest(APRequests, stop);
  const parcelData =
    !stop.parcel && !APRequests.length
      ? []
      : [...stop.parcel, ...additionalParcels];

  const smallsToteParcel = isSmallsToteParcel({ parcel: parcelData });
  const highValue = isHighValueParcel({ parcel: parcelData });
  const pharmaParcel = isPharmaParcel({ parcel: parcelData });

  if (smallsToteParcel) {
    values.push(
      <div
        className={clsx([
          styles.highValueContainer,
          styles.cellOrange,
          styles.roundLeftBorder,
        ])}
        data-testid="smalls-tote-mark"
      />
    );
    tipItems.push(TOTE_PARCEL);
  }

  if (highValue) {
    values.push(
      <div
        className={clsx([styles.highValueContainer, styles.cellBlue], {
          [styles.roundLeftBorder]: !smallsToteParcel,
        })}
        data-testid="high-value-mark"
      />
    );
    tipItems.push(HIGH_VALUE_PARCEL);
  }

  if (pharmaParcel) {
    values.push(
      <div
        className={clsx([styles.highValueContainer, styles.cellGreen], {
          [styles.roundLeftBorder]: !smallsToteParcel && !highValue,
        })}
        data-testid="pharma-parcel-mark"
      />
    );
    tipItems.push(PHARMA_PARCEL);
  }

  return values.length ? (
    <Tooltip
      title={
        <div style={{ whiteSpace: 'pre-line' }}>{tipItems.join('\n')}</div>
      }
      placement="top-start"
      fontSize="small"
    >
      <div
        className={clsx(styles.fullHeighContainer, styles.leftContent, {
          notEmpty: tipItems.length > 1,
        })}
      >
        {values}
      </div>
    </Tooltip>
  ) : (
    ''
  );
});

const StopMarker = ({
  feature,
  isSelected,
  showRequestPopup,
  withAnchor,
  itemRef,
}) => {
  const styles = useStyles();
  const zoomLevel = useSelector(getZoomLevel);

  const stop = useMemo(() => feature.properties.stop, [feature]);
  const isOuterZoom = useMemo(() => zoomLevel < 13, [zoomLevel]);
  const isCollection = useMemo(() => isCollectionStop(stop), [stop]);

  const color = useMemo(
    () => (!isCollection ? getRagStatusColor(stop.ragStatus) : palette.slate),
    [isCollection, stop]
  );

  const stopType = useMemo(() => calculateDeliveryStopType(stop), [stop]);

  const isStandardStop = useMemo(
    () => stopType === DELIVERY_STOP_TYPES.STANDARD,
    [stopType]
  );

  const hasRouteOutstandingRequest = useMemo(
    () => !!stop?.parcel && !!getRouteOutstandingRequest(stop.parcel).length,
    [stop]
  );

  const showExceptionContainer = useMemo(
    () =>
      !isOuterZoom &&
      (!!stop.requests.length ||
        hasAnyExceptions(stop) ||
        hasRouteOutstandingRequest),
    [stop, isOuterZoom, hasRouteOutstandingRequest]
  );

  const handleOnExceptionClick = useCallback(
    (e) => {
      if (stop.requests.length) {
        e.stopPropagation();
        showRequestPopup(feature);
      }
    },
    [feature, showRequestPopup, stop]
  );

  const getTestId = useMemo(() => {
    const hasRequest = !!stop.requests.length;
    if (!hasRequest) {
      return 'exception';
    }
    let result = 'request';
    const requestType = getStopRequestType(stop.requests);
    switch (requestType.type) {
      case requestIconType.MIXED:
        return result + '-mixed';
      case requestIconType.ADD:
        result += '-add';
        if (isCollection) {
          result += '-collection';
        }
        return result;
      case requestIconType.REMOVE:
        result += '-remove';
        if (isCollection) {
          result += '-collection';
        }
        return result;
      case requestIconType.DEFFER:
        result += '-deffer';
        if (isCollection) {
          result += '-collection';
        }
        return result;
      default:
        return result;
    }
  }, [stop, isCollection]);

  const markerStopNumber = useMemo(() => stop.displayNumber, [stop]);

  const getCollectionIcon = useCallback(() => {
    const pickupKind = getStopPickupKind(stop);
    if (pickupKind && pickupKindTypesLabels[pickupKind] && !isOuterZoom) {
      return (
        <Text
          noWrap
          className={clsx(
            styles.textValue,
            isOuterZoom
              ? styles.smallContainerTextValue
              : styles.detailedContainerTextValue
          )}
          data-testid={`${pickupKind}-stop-marker`}
        >
          {pickupKindTypesLabels[pickupKind]}
        </Text>
      );
    }
    return <Collection className={styles.collectionIcon} />;
  }, []);

  const getStopTitleElement = useCallback(() => {
    const isLongText = !isOuterZoom && stopType.length > 9;
    if (isStandardStop && !isOuterZoom) {
      return (
        <View className={styles.dpdIcon} data-testid="standard-stop-marker">
          <ParcelIcon />
        </View>
      );
    }
    const pickupKind = getStopPickupKind(stop);
    return (
      <Text
        noWrap
        className={clsx(
          styles.textValue,
          isOuterZoom
            ? styles.smallContainerTextValue
            : styles.detailedContainerTextValue,
          {
            smallText: isLongText,
          }
        )}
        data-testid={`${(pickupKind && pickupKindTypesLabels[pickupKind]) || stopType}-stop-marker`}
      >
        {isOuterZoom
          ? markerStopNumber
          : (pickupKind && pickupKindTypesLabels[pickupKind]) || stopType}
      </Text>
    );
  }, [
    isOuterZoom,
    isStandardStop,
    markerStopNumber,
    stopType,
    styles.detailedContainerTextValue,
    styles.dpdIcon,
    styles.smallContainerTextValue,
    styles.textValue,
  ]);

  return (
    <StyledBadge
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      invisible={isOuterZoom ? !isCollection : isOuterZoom}
      badgeContent={markerStopNumber}
      max={100000000}
      showZero
      ref={itemRef}
    >
      <MarkerContainer
        data-testid={`stop-${stop.stopId}`}
        className={isSelected && styles.activeContainer}
        withAnchor={withAnchor}
      >
        <View
          style={{ backgroundColor: color || palette.dpdRed }}
          className={clsx(
            styles.container,
            isOuterZoom ? styles.smallContainer : styles.detailedContainer
          )}
        >
          {!isOuterZoom && <HighValueItemContainer stop={stop} />}
          {isCollection ? getCollectionIcon() : getStopTitleElement()}
          {showExceptionContainer && (
            <ConditionalWrapper
              condition={hasRouteOutstandingRequest}
              wrapper={(children) => (
                <Tooltip
                  placement="top-start"
                  fontSize="small"
                  title={`An outstanding request on route(s): ${getRouteOutstandingRequest(
                    stop.parcel
                  ).join(', ')}`}
                >
                  {children}
                </Tooltip>
              )}
            >
              <div
                className={clsx([
                  styles.fullHeighContainer,
                  styles.exceptionWrapper,
                ])}
              >
                <View
                  className={clsx(
                    styles.exceptionContainer,
                    styles.rightContent,
                    hasRouteOutstandingRequest && styles.yellowContainer
                  )}
                  data-testid={getTestId}
                  onClick={handleOnExceptionClick}
                >
                  <ExceptionContainer stop={stop} />
                </View>
              </div>
            </ConditionalWrapper>
          )}
        </View>
      </MarkerContainer>
    </StyledBadge>
  );
};

export default memo(StopMarker);
